import Typography from "components/elements/Typography";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import classes from "./classes.module.scss";
import { useCallback, useEffect, useState } from "react";
import ProjectTeamResponseResource from "common/resources/Project/Config/Team/ProjectTeamResponseResource";
import { useProjectId } from "hooks/useProjectId";
import { container } from "tsyringe";
import TeamService from "services/projects/TeamService";
import { useContributor } from "components/pages/ProfessionalSpace/hooks/useContributor";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Form, { useForm } from "components/materials/Form";
import ContributorRequestResource from "common/resources/Project/Config/Contributor/ContributorRequestResource";
import ProjectLeaderService from "services/projects/ProjectLeaderService";
import { EToastType, useToast } from "contexts/Toast";

const projectTeamService = container.resolve(TeamService);
const projectLeaderService = container.resolve(ProjectLeaderService);

type FormData = {
	[key: string]: unknown;
};
type IProps = {
	projectLeader: ContributorResponseResource;
	projectId: string;
	projectTeamId: Pick<ProjectTeamResponseResource, "id">["id"];
};

function ProjectLeaderContent(props: IProps) {
	const { showToast } = useToast();
	const { avatarFile } = useContributor(props.projectLeader);
	const [isLoading, setIsLoading] = useState(false);
	const [hasDataChanged, setHasDataChanged] = useState(false);
	const { formRef, errors, handleError, clearErrors } = useForm();

	const onDataChange = useCallback(() => {
		if (hasDataChanged) return;
		setHasDataChanged(true);
	}, [hasDataChanged]);

	const validateProjectLeader = useCallback((formData: FormData) => {
		const contributorResource = ContributorRequestResource.hydrate<ContributorRequestResource>({
			firstName: formData["firstName"] as string,
			lastName: formData["lastName"] as string,
			companyName: formData["companyName"] as string,
		});

		return contributorResource.validateOrReject();
	}, []);

	const onSubmit = useCallback(
		async (formData: FormData) => {
			try {
				setIsLoading(true);
				setHasDataChanged(false);
				clearErrors();
				const projectLeader = await validateProjectLeader(formData);
				const projectLeaderAvatar = (formData["leaderPicture"] as FileList)?.[0];
				await projectLeaderService.update(props.projectId, props.projectTeamId, props.projectLeader.id, projectLeader, projectLeaderAvatar);
				setIsLoading(false);
				showToast({ message: I18n.asset.pages.professional_space.tabs.information.saved, type: EToastType.SUCCESS });
			} catch (error) {
				handleError(error);
				setIsLoading(false);
			}
		},
		[props, clearErrors, handleError, showToast, validateProjectLeader],
	);

	return (
		<Form className={classes["root"]} onChange={onDataChange} formRef={formRef} onSubmit={onSubmit} errors={errors}>
			<div className={classes["header-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.information.sub_tabs.project_leader}
				</Typography>
				<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading} disabled={!hasDataChanged}>
					{I18n.asset.pages.professional_space.tabs.information.save}
				</Button>
			</div>

			<div className={classes["text"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.information.sub_tabs.project_leader_subtitle}
				</Typography>
				<Typography type="p" size="medium" weight="regular">
					{I18n.asset.pages.professional_space.tabs.information.sub_tabs.project_leader_description}
				</Typography>
			</div>

			<div className={classes["form"]}>
				<div className={classes["group"]}>
					<Input
						name="firstName"
						defaultValue={props.projectLeader.firstName}
						label={I18n.asset.pages.professional_space.tabs.information.project_info.first_name}
						className={classes["input"]}
					/>
					<Input
						name="lastName"
						defaultValue={props.projectLeader.lastName}
						label={I18n.asset.pages.professional_space.tabs.information.project_info.last_name}
						className={classes["input"]}
					/>
				</div>
				<Input name="companyName" defaultValue={props.projectLeader.companyName} label={I18n.asset.pages.professional_space.tabs.information.project_info.enterprise} />
				<DragAndDropFile
					name="leaderPicture"
					defaultFile={avatarFile}
					formRef={formRef}
					accept={["image/png", "image/jpeg", "image/webp"]}
					sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
					maxMbSize={5}
					description={I18n.asset.pages.professional_space.tabs.information.project_info.drag_and_drop_profile_picture}
					label={I18n.asset.pages.professional_space.tabs.information.project_info.profile_picture}
					keyErrorName={"sha256"}
				/>
				<Input name="atLeastOne" hidden className={classes["hidden"]} />
			</div>
		</Form>
	);
}

export default function ProjectLeader() {
	const { projectId } = useProjectId();
	const [projectTeam, setProjectTeam] = useState<ProjectTeamResponseResource | null>(null);

	useEffect(() => {
		projectTeamService.getByProjectId(projectId).then(setProjectTeam).catch(console.error);
	}, [projectId]);

	if (!projectTeam?.projectLeader) return null;
	return <ProjectLeaderContent projectLeader={projectTeam.projectLeader} projectId={projectId} projectTeamId={projectTeam.id} />;
}
